/**
 * Context menu — right-click bulk-op menu for the card pane + PDF overlays.
 *
 * Right-clicking anywhere on a selected card or selected overlay opens this
 * menu. Operations are legally constrained before they appear:
 *
 *   - Merge: enabled only when 2+ selected, all same type, contiguous in the
 *            card-list document order.
 *   - Change type → submenu of legal targets (intersection across selection).
 *   - Group under new parent: enabled when 2+ selected; mixed types allowed.
 *   - Delete: enabled when ≥1 selected; confirms if any item has authored content.
 *   - Insert new element (empty PDF area only): wired through insert-flow.
 *
 * The hierarchical Change-type list reuses `cards.js#ROLE_TREE` as single source of truth.
 *
 * Public API:
 *   openContextMenu({ x, y, mode, ... })  → opens at viewport coords
 *   closeContextMenu()                    → idempotent close
 *   wireContextMenu({ ... })              → installs contextmenu listeners
 *
 * @module context-menu
 */
import { ROLE_TREE } from "./cards.js";
let _activeMenu = null;
let _activeOnDoc = null;
let _activeOnKey = null;
/** Resolve a lookup that is either a function or a Map. */
function lookup(src, id) {
    if (src == null)
        return null;
    if (typeof src === "function")
        return src(id);
    if (src instanceof Map)
        return src.get(id);
    // Defensive: accept any Map-shaped duck-type. The historical API supported
    // arbitrary `{ get(id) }` objects; preserve that here.
    const getter = src.get;
    return typeof getter === "function" ? getter.call(src, id) : null;
}
/**
 * Determine whether selection is contiguous in the supplied ordered id list.
 * Contiguous = the selected positions form a single run with no gaps.
 */
export function isContiguous(orderedIds, selected) {
    if (!Array.isArray(orderedIds) || !selected || selected.size === 0)
        return false;
    if (selected.size === 1)
        return true;
    const indices = [];
    for (let i = 0; i < orderedIds.length; i++) {
        if (selected.has(orderedIds[i]))
            indices.push(i); // WHY: loop invariant — i < orderedIds.length
    }
    if (indices.length !== selected.size)
        return false; // selection not all in list
    for (let i = 1; i < indices.length; i++) {
        if (indices[i] !== indices[i - 1] + 1)
            return false; // WHY: loop invariant — i < indices.length, i-1 >= 0
    }
    return true;
}
/**
 * Return whether the selection is type-homogeneous given a structId→role map.
 */
export function isHomogeneous(selected, roleByStructId) {
    if (!selected || selected.size === 0)
        return { same: false, role: null };
    let common = null;
    for (const id of selected) {
        const role = lookup(roleByStructId, id);
        if (common === null)
            common = role ?? "";
        else if ((role ?? "") !== common)
            return { same: false, role: null };
    }
    return { same: true, role: common };
}
/**
 * Compute "selection has authored content" — used to gate the delete confirm.
 *
 * Authored content = non-empty alt text or a non-empty Caption / Note / Span
 * text. The card lookup yields whatever `cards.js#blockToCard` produced.
 */
export function selectionHasAuthoredContent(selected, cardByStructId) {
    if (!selected || selected.size === 0)
        return false;
    for (const id of selected) {
        const card = lookup(cardByStructId, id);
        if (!card)
            continue;
        if (card.alt && String(card.alt).trim() !== "")
            return true;
        // Caption / Note / Span carry their text in `text`; H/P/etc. count too,
        // but for those the user typically WANTS the delete to be quick. The
        // confirm ONLY fires for alt-text-bearing roles per the brief.
        if (card.role === "Figure" && card.alt && String(card.alt).trim() !== "")
            return true;
    }
    return false;
}
// ─── Menu DOM ──────────────────────────────────────────────────────────────
/**
 * Open the context menu at viewport coords (x, y).
 *
 * Callback for the dispatcher in editor.js. Actions:
 *   "merge"          — payload: undefined
 *   "change-type"    — payload: roleToken
 *   "group"          — payload: undefined
 *   "delete"         — payload: undefined
 *   "insert"         — payload: undefined
 */
export function openContextMenu(opts) {
    closeContextMenu();
    const { x, y, mode = "selection", selected = new Set(), orderedIds = [], roleByStructId = null, cardByStructId = null, onAction, } = opts;
    const menu = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    menu.className = "editor-ctx-menu is-open";
    menu.setAttribute("role", "menu");
    if (mode === "empty") {
        menu.appendChild(buildEmptyAreaMenu(onAction));
    }
    else {
        menu.appendChild(buildSelectionMenu({
            selected, orderedIds, roleByStructId, cardByStructId, onAction,
        }));
    }
    document.body.appendChild(menu); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    // Position. Estimate menu size before measuring; on jsdom getBoundingClientRect
    // returns zero, so we use a 220×260 budget.
    const w = (menu.offsetWidth && menu.offsetWidth > 0) ? menu.offsetWidth : 220;
    const h = (menu.offsetHeight && menu.offsetHeight > 0) ? menu.offsetHeight : 260;
    const winW = (typeof window !== "undefined" && window.innerWidth) || 1024; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const winH = (typeof window !== "undefined" && window.innerHeight) || 768; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    menu.style.left = `${Math.min(Math.max(0, x), Math.max(0, winW - w))}px`;
    menu.style.top = `${Math.min(Math.max(0, y), Math.max(0, winH - h))}px`;
    _activeMenu = menu;
    // Outside-click + Escape close. Use timeouts so the right-click event that
    // opened the menu doesn't immediately close it.
    setTimeout(() => {
        _activeOnDoc = (ev) => {
            const target = ev.target;
            if (target instanceof Node && !menu.contains(target))
                closeContextMenu();
            else if (!(target instanceof Node))
                closeContextMenu();
        };
        document.addEventListener("mousedown", _activeOnDoc, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        _activeOnKey = (ev) => {
            if (ev.key === "Escape")
                closeContextMenu();
        };
        document.addEventListener("keydown", _activeOnKey, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    }, 0);
    return menu;
}
export function closeContextMenu() {
    if (_activeMenu?.parentNode) {
        _activeMenu.parentNode.removeChild(_activeMenu);
    }
    _activeMenu = null;
    if (_activeOnDoc) {
        document.removeEventListener("mousedown", _activeOnDoc, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        _activeOnDoc = null;
    }
    if (_activeOnKey) {
        document.removeEventListener("keydown", _activeOnKey, true); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        _activeOnKey = null;
    }
}
/** Test helper: is the menu currently mounted? */
export function _activeMenuForTest() { return _activeMenu; }
function buildSelectionMenu({ selected, orderedIds, roleByStructId, onAction }) {
    const frag = document.createDocumentFragment(); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    const size = selected.size;
    const homog = isHomogeneous(selected, roleByStructId);
    const contig = isContiguous(orderedIds, selected);
    const canMerge = size >= 2 && homog.same && contig;
    const canChangeType = size >= 1;
    const canGroup = size >= 1; // mixed-type allowed per UX memo §7.3
    const canDelete = size >= 1;
    const mergeReason = !canMerge ? (size < 2 ? "Select 2+ elements to merge"
        : !homog.same ? "Merge requires elements of the same type"
            : "Merge requires contiguous siblings (no gaps in selection)") : "";
    frag.appendChild(makeItem({
        label: "Merge",
        shortcut: "",
        disabled: !canMerge,
        title: mergeReason,
        onClick: () => { closeContextMenu(); if (onAction)
            onAction("merge"); },
    }));
    // Change type → submenu of legal types
    frag.appendChild(makeChangeTypeItem({
        disabled: !canChangeType,
        currentRole: homog.same ? homog.role : null,
        onPick: (roleToken) => { closeContextMenu(); if (onAction)
            onAction("change-type", roleToken); },
    }));
    frag.appendChild(makeSep());
    frag.appendChild(makeItem({
        label: "Group under new parent",
        shortcut: "",
        disabled: !canGroup,
        title: !canGroup ? "Select 1+ elements to group" : "",
        onClick: () => { closeContextMenu(); if (onAction)
            onAction("group"); },
    }));
    frag.appendChild(makeSep());
    // Delete is reversible via Cmd-Z — no confirm dialog. Per
    // docs/ux/editor-philosophy.md, every reversible
    // gesture proceeds silently and the toolbar Undo button is the
    // safety net. selectionHasAuthoredContent is no longer consulted
    // here, but is retained as an exported helper for future
    // analytics / surfaces (e.g., a non-blocking "deleted alt text"
    // toast) without re-introducing the friction.
    frag.appendChild(makeItem({
        label: "Delete",
        shortcut: "⌫",
        disabled: !canDelete,
        danger: true,
        onClick: () => {
            closeContextMenu();
            if (onAction)
                onAction("delete");
        },
    }));
    return frag;
}
/**
 * Heading levels the "Insert heading" submenu exposes. Single source of truth
 * shared by `makeHeadingSubmenuItem` (the DOM builder) and
 * `emptyAreaInsertRoles` (the exposed-role SSOT the CRP-1 parity walk reads).
 */
const HEADING_LEVELS = Object.freeze([1, 2, 3, 4, 5, 6]);
/**
 * Empty-area right-click insert items — the THIRD role-exposing surface (after
 * the type popover's `ROLE_TREE` and `coerceRole`; editor-create-role-parity).
 *
 * EXPOSED ⊆ SUPPORTED (invariant CRP-1): every role emitted here MUST have a
 * backing IR block kind in `buildBlockForRole`, or clicking it silently creates
 * a mislabeled Paragraph (the List-role bug class). "Insert list item" (`LI` — a
 * category error; list items live inside a List) and "Insert quote"
 * (`BlockQuote` — no IR block kind) were removed for exactly that reason
 * (Phase 3), mirroring the Phase-2 phantom removal from `ROLE_TREE`. The
 * `office-op-parity`-style walk in `create-role-parity.seam.test.ts` reads
 * `emptyAreaInsertRoles()` and fails if a future entry re-introduces the drift.
 *
 * Order follows usage frequency on staging (paragraph dwarfs everything else;
 * headings second; caption/note the long tail).
 */
export const EMPTY_AREA_INSERT_ITEMS = Object.freeze([
    { label: "Insert paragraph", role: "P" },
    // Heading expands to a submenu (H1-H6) — see makeHeadingSubmenuItem.
    { label: "Insert heading", submenu: "headings" },
    { label: "Insert caption", role: "Caption" },
    { label: "Insert note", role: "Note" },
]);
/**
 * The concrete role tokens the empty-area insert menu can emit — the exposed
 * source of truth walked by the CRP-1 parity control (rule #33 / #42: walk the
 * data, never snapshot it). Direct-item roles plus H1–H6 for the headings
 * submenu.
 */
export function emptyAreaInsertRoles() {
    const roles = [];
    for (const item of EMPTY_AREA_INSERT_ITEMS) {
        if (item.submenu === "headings") {
            for (const lvl of HEADING_LEVELS)
                roles.push(`H${lvl}`);
        }
        else if (item.role) {
            roles.push(item.role);
        }
    }
    return roles;
}
function buildEmptyAreaMenu(onAction) {
    const frag = document.createDocumentFragment(); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    for (const item of EMPTY_AREA_INSERT_ITEMS) {
        if (item.submenu === "headings") {
            frag.appendChild(makeHeadingSubmenuItem({
                onPick: (role) => { closeContextMenu(); if (onAction)
                    onAction("insert-with-role", { role }); },
            }));
            continue;
        }
        const role = item.role;
        frag.appendChild(makeItem({
            label: item.label,
            shortcut: "",
            disabled: false,
            onClick: () => { closeContextMenu(); if (onAction && role)
                onAction("insert-with-role", { role }); },
        }));
    }
    frag.appendChild(makeSep());
    // Free-form draw fallback — preserves the original "let me drag" path
    // for users who want a non-standard size or a role outside the
    // pre-picked list.
    frag.appendChild(makeItem({
        label: "Insert other (drag to draw)",
        shortcut: "",
        disabled: false,
        title: "Drop a default-sized box you can re-tune; pick any role via the popover",
        onClick: () => { closeContextMenu(); if (onAction)
            onAction("insert"); },
    }));
    frag.appendChild(makeSep());
    frag.appendChild(makeItem({
        label: "Cancel",
        shortcut: "Esc",
        disabled: false,
        onClick: () => closeContextMenu(),
    }));
    return frag;
}
/**
 * Headings submenu item — hover or click on "Insert heading" reveals
 * H1-H6 picker. Mirrors the change-type submenu but with insert-side
 * dispatch.
 */
function makeHeadingSubmenuItem({ onPick }) {
    const wrap = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    wrap.className = "editor-ctx-item editor-ctx-has-sub";
    wrap.setAttribute("role", "menuitem");
    const lbl = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    lbl.className = "editor-ctx-item-label";
    lbl.textContent = "Insert heading";
    wrap.appendChild(lbl);
    const arrow = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    arrow.className = "editor-ctx-item-arrow";
    arrow.textContent = "▶";
    wrap.appendChild(arrow);
    const sub = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    sub.className = "editor-ctx-submenu";
    for (const lvl of HEADING_LEVELS) {
        const role = `H${lvl}`;
        const opt = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        opt.type = "button";
        opt.className = "editor-ctx-type-option";
        opt.dataset.role = role;
        const chip = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        chip.className = "editor-ctx-type-option-chip";
        chip.dataset.role = role;
        chip.textContent = role;
        opt.appendChild(chip);
        const tx = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        tx.className = "editor-ctx-type-option-text";
        const name = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        name.className = "editor-ctx-type-option-name";
        name.textContent = `Heading ${lvl}`;
        tx.appendChild(name);
        opt.appendChild(tx);
        opt.addEventListener("click", (ev) => {
            ev.stopPropagation();
            if (typeof onPick === "function")
                onPick(role);
        });
        sub.appendChild(opt);
    }
    wrap.appendChild(sub);
    return wrap;
}
function makeItem({ label, shortcut, disabled, danger, title, onClick }) {
    const it = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    it.type = "button";
    it.className = "editor-ctx-item";
    if (disabled)
        it.classList.add("is-disabled");
    if (danger)
        it.classList.add("is-danger");
    if (title)
        it.setAttribute("title", title);
    it.setAttribute("role", "menuitem");
    const lbl = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    lbl.className = "editor-ctx-item-label";
    lbl.textContent = label;
    it.appendChild(lbl);
    if (shortcut) {
        const sc = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        sc.className = "editor-ctx-item-shortcut";
        sc.textContent = shortcut;
        it.appendChild(sc);
    }
    it.addEventListener("click", (ev) => {
        ev.stopPropagation();
        if (it.classList?.contains("is-disabled"))
            return;
        if (typeof onClick === "function")
            onClick();
    });
    return it;
}
function makeSep() {
    const s = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    s.className = "editor-ctx-sep";
    s.setAttribute("role", "separator");
    return s;
}
function makeChangeTypeItem({ disabled, currentRole, onPick }) {
    const wrap = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    wrap.className = "editor-ctx-item editor-ctx-has-sub";
    if (disabled)
        wrap.classList.add("is-disabled");
    wrap.setAttribute("role", "menuitem");
    const lbl = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    lbl.className = "editor-ctx-item-label";
    lbl.textContent = "Change type";
    wrap.appendChild(lbl);
    const arrow = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    arrow.className = "editor-ctx-item-arrow";
    arrow.textContent = "▶";
    wrap.appendChild(arrow);
    const sub = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    sub.className = "editor-ctx-submenu";
    // Render ROLE_TREE inline so the submenu uses the same hierarchy as the
    // single-card popover (UX memo §7.4 — one source of truth).
    let isFirst = true;
    for (const sec of ROLE_TREE) {
        const header = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        header.className = "editor-ctx-section-header";
        if (isFirst)
            header.classList.add("is-first");
        header.textContent = sec.section;
        sub.appendChild(header);
        isFirst = false;
        for (const item of sec.items) {
            const opt = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            opt.type = "button";
            opt.className = "editor-ctx-type-option";
            opt.dataset.role = item.role;
            if (item.role === currentRole)
                opt.classList.add("is-current");
            const chip = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            chip.className = "editor-ctx-type-option-chip";
            chip.dataset.role = item.role;
            chip.textContent = item.label;
            opt.appendChild(chip);
            const tx = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            tx.className = "editor-ctx-type-option-text";
            const name = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            name.className = "editor-ctx-type-option-name";
            name.textContent = item.label;
            const desc = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            desc.className = "editor-ctx-type-option-desc";
            desc.textContent = item.desc;
            tx.appendChild(name);
            tx.appendChild(desc);
            opt.appendChild(tx);
            opt.addEventListener("click", (ev) => {
                ev.stopPropagation();
                if (wrap.classList?.contains("is-disabled"))
                    return;
                if (typeof onPick === "function")
                    onPick(item.role);
            });
            sub.appendChild(opt);
        }
    }
    wrap.appendChild(sub);
    return wrap;
}
// ─── Wiring ────────────────────────────────────────────────────────────────
/**
 * Install contextmenu listeners on the card list + overlay container.
 *
 * The host promises:
 *   - `getSelected()` is the latest selection set (Set<structId>).
 *   - `getOrderedIds()` is the card-list ordering.
 *   - `roleByStructId` / `cardByStructId` are up-to-date lookups.
 *
 * On right-click on a card or overlay:
 *   - If the target struct id is NOT in the selection, replace selection with
 *     just that id (Finder semantics — right-click selects the target).
 *   - Open the context menu in selection mode.
 *
 * On right-click on the overlay container's empty area:
 *   - Open the menu in "empty" mode (insert-only).
 */
export function wireContextMenu(opts) {
    const { cardListEl, overlayContainerEl, getLinkedViews, getSelected, getOrderedIds, getRoleByStructId, getCardByStructId, onAction, } = opts;
    function onCtx(ev) {
        const t = ev.target;
        const tEl = (t instanceof Element) ? t : null;
        const card = tEl ? tEl.closest(".editor-card") : null;
        const overlay = tEl ? tEl.closest(".editor-overlay-bbox") : null;
        const targetEl = card ?? overlay;
        const lv = getLinkedViews?.();
        if (targetEl?.dataset?.structId) {
            ev.preventDefault();
            const id = targetEl.dataset.structId;
            const sel = getSelected ? getSelected() : new Set();
            if (!sel.has(id) && lv) {
                lv.selectStructId(id, "single");
            }
            openContextMenu({
                x: ev.clientX || 0,
                y: ev.clientY || 0,
                mode: "selection",
                selected: getSelected ? getSelected() : new Set(),
                orderedIds: getOrderedIds ? getOrderedIds() : [],
                roleByStructId: getRoleByStructId?.(),
                cardByStructId: getCardByStructId?.(),
                onAction,
            });
            return;
        }
        // Empty PDF area: only fire when the right-click is on the overlay
        // container itself (not on a card, not on the canvas hard surface).
        // We also walk up from the target — multi-page mode renders inside
        // a `.editor-pdf-pageframe`, but the overlay element registered
        // here may be the per-page overlay, not the click target.
        const onEmptyOverlay = overlayContainerEl && (t === overlayContainerEl ||
            (t instanceof Node && t.parentNode === overlayContainerEl));
        if (onEmptyOverlay && overlayContainerEl) {
            ev.preventDefault();
            // Capture the click point in canvas-px so the dispatch can
            // anchor the new bbox there. The overlay element is the canvas
            // for our purposes (it overlays it 1:1).
            const rect = overlayContainerEl.getBoundingClientRect();
            const clickPt = {
                x: (ev.clientX || 0) - rect.left,
                y: (ev.clientY || 0) - rect.top,
            };
            openContextMenu({
                x: ev.clientX || 0,
                y: ev.clientY || 0,
                mode: "empty",
                onAction: (action, payload) => {
                    if (typeof onAction === "function") {
                        // Forward the click point alongside the role payload
                        // so the host can drop the new bbox at the cursor.
                        if (action === "insert-with-role" || action === "insert") {
                            const p = payload || {};
                            onAction(action, { ...p, clickPt });
                        }
                        else {
                            onAction(action, payload);
                        }
                    }
                },
            });
        }
    }
    if (cardListEl)
        cardListEl.addEventListener("contextmenu", onCtx);
    if (overlayContainerEl)
        overlayContainerEl.addEventListener("contextmenu", onCtx);
    // Touch-device long-press fallback (audit F-05). iOS Safari does not fire
    // `contextmenu` on long-press for most non-interactive targets — it shows
    // the system text-selection callout instead. We detect a stationary
    // ~500ms touch and synthesise a contextmenu-shaped event so iPad users
    // get the same right-click menu as desktop users.
    const LONG_PRESS_MS = 500;
    const MOVE_TOLERANCE_PX = 8;
    let _lpTimer = null;
    let _lpStart = null;
    function clearLp() {
        if (_lpTimer) {
            clearTimeout(_lpTimer);
            _lpTimer = null;
        }
        _lpStart = null;
    }
    function onTouchStart(ev) {
        if (ev.touches.length !== 1) {
            clearLp();
            return;
        }
        const t = ev.touches[0]; // WHY: ev.touches.length === 1 check above guarantees index 0 is present
        _lpStart = { x: t.clientX, y: t.clientY, target: ev.target };
        _lpTimer = setTimeout(() => {
            if (!_lpStart)
                return;
            // Synthesise a contextmenu-shaped object for onCtx. We can't
            // dispatch a real `contextmenu` Event because some iOS WebKit
            // builds suppress preventDefault on synthesised events; just
            // call onCtx directly with the same shape it expects.
            onCtx({
                target: _lpStart.target,
                clientX: _lpStart.x,
                clientY: _lpStart.y,
                preventDefault: () => { },
            });
            clearLp();
        }, LONG_PRESS_MS);
    }
    function onTouchMove(ev) {
        if (!_lpStart)
            return;
        const t = ev.touches[0];
        if (!t) {
            clearLp();
            return;
        }
        const dx = Math.abs(t.clientX - _lpStart.x);
        const dy = Math.abs(t.clientY - _lpStart.y);
        if (dx > MOVE_TOLERANCE_PX || dy > MOVE_TOLERANCE_PX)
            clearLp();
    }
    if (cardListEl) {
        cardListEl.addEventListener("touchstart", onTouchStart, { passive: true });
        cardListEl.addEventListener("touchmove", onTouchMove, { passive: true });
        cardListEl.addEventListener("touchend", clearLp, { passive: true });
        cardListEl.addEventListener("touchcancel", clearLp, { passive: true });
    }
    if (overlayContainerEl) {
        overlayContainerEl.addEventListener("touchstart", onTouchStart, { passive: true });
        overlayContainerEl.addEventListener("touchmove", onTouchMove, { passive: true });
        overlayContainerEl.addEventListener("touchend", clearLp, { passive: true });
        overlayContainerEl.addEventListener("touchcancel", clearLp, { passive: true });
    }
    return function detach() {
        if (cardListEl) {
            cardListEl.removeEventListener("contextmenu", onCtx);
            cardListEl.removeEventListener("touchstart", onTouchStart);
            cardListEl.removeEventListener("touchmove", onTouchMove);
            cardListEl.removeEventListener("touchend", clearLp);
            cardListEl.removeEventListener("touchcancel", clearLp);
        }
        if (overlayContainerEl) {
            overlayContainerEl.removeEventListener("contextmenu", onCtx);
            overlayContainerEl.removeEventListener("touchstart", onTouchStart);
            overlayContainerEl.removeEventListener("touchmove", onTouchMove);
            overlayContainerEl.removeEventListener("touchend", clearLp);
            overlayContainerEl.removeEventListener("touchcancel", clearLp);
        }
        clearLp();
        closeContextMenu();
    };
}
